import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Award, Download, FileCheck, Save } from "lucide-react";
import { fmtDate } from "@/lib/format-date";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { type CertSettings } from "@/lib/certificate";
import { getCertificateTemplate, saveCertificateTemplate } from "@/lib/certificate-template-api";
import {
  listEligibleMembers,
  listIssuedCertificates,
  downloadCertificatePdf,
  type EligibleMember,
} from "@/lib/certificates-api";

export const Route = createFileRoute("/admin/certificates")({ component: AdminCerts });

const BLANK_SETTINGS: CertSettings = {
  title_en: "",
  title_hi: "",
  title_gu: "",
  message_en: "",
  message_hi: "",
  message_gu: "",
  signatory_name: "",
  signatory_title_en: "",
  signatory_title_hi: "",
  signatory_title_gu: "",
};

function AdminCerts() {
  const { user } = useAuth();
  const [tab, setTab] = useState<"issue" | "issued" | "settings">("issue");
  const [settings, setSettings] = useState<CertSettings | null>(BLANK_SETTINGS);

  // Eligible members (issue tab) — bound to the real API.
  const {
    data: eligible = [],
    isLoading: eligLoading,
    isError: eligError,
    error: eligErr,
  } = useQuery({
    queryKey: ["jgy-cert-eligible"],
    queryFn: listEligibleMembers,
  });

  // Issued certificates (issued tab) — bound to the real API.
  const {
    data: certs = [],
    isLoading: issuedLoading,
    isError: issuedError,
    error: issuedErr,
  } = useQuery({
    queryKey: ["jgy-cert-issued"],
    queryFn: listIssuedCertificates,
  });

  // Only surface certificates for members who completed every temple.
  const completedCerts = certs.filter((c) => c.total > 0 && c.visited >= c.total);

  // Certificate template (settings tab) — bound to the real API.
  const { data: template } = useQuery({
    queryKey: ["jgy-cert-template"],
    queryFn: getCertificateTemplate,
  });
  useEffect(() => {
    if (template) {
      setSettings(template);
    }
  }, [template]);

  const saveMut = useMutation({
    mutationFn: (s: CertSettings) => saveCertificateTemplate(s),
    onSuccess: (saved) => {
      setSettings(saved);
      toast.success("Template saved");
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Save failed"),
  });

  const issue = async (_m: EligibleMember) => {
    if (!user) {
      return;
    }
    // TODO: bind Certificate issue API (endpoint not provided yet)
    toast("Certificate issue API not connected yet");
  };

  const downloadPdf = (url: string | null, certNo: string) => {
    if (!url) {
      toast.error("No certificate PDF available");
      return;
    }
    downloadCertificatePdf(url, `${certNo || "certificate"}.pdf`).catch((e) =>
      toast.error(e instanceof Error ? e.message : "Download failed"),
    );
  };

  const saveSettings = () => {
    if (!settings) {
      return;
    }
    saveMut.mutate(settings);
  };

  return (
    <div className="mx-auto min-w-0 max-w-6xl">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="font-serif text-2xl font-bold">Certificates</h1>
          <p className="text-sm text-muted-foreground">
            Issue and manage Yatra completion certificates
          </p>
        </div>
      </div>

      <div className="mb-4 inline-flex rounded-xl border border-border bg-card p-1">
        {(["issue", "issued", "settings"] as const).map((k) => (
          <button
            key={k}
            onClick={() => setTab(k)}
            className={`rounded-lg px-4 py-1.5 text-sm font-semibold capitalize ${tab === k ? "text-primary-foreground" : "text-muted-foreground"}`}
            style={tab === k ? { background: "var(--gradient-saffron)" } : undefined}
          >
            {k === "issue" ? "Eligible Members" : k === "issued" ? "Issued" : "Template Settings"}
          </button>
        ))}
      </div>

      {tab === "issue" && (
        <div className="-mx-4 overflow-hidden rounded-2xl border border-border bg-card sm:mx-0">
          <div className="overflow-x-auto">
            <div className="min-w-[520px]">
              <div className="grid grid-cols-12 gap-2 border-b border-border px-4 py-3 text-xs font-semibold text-muted-foreground">
                <div className="col-span-4">Name</div>
                <div className="col-span-3">Phone</div>
                <div className="col-span-2">Progress</div>
                <div className="col-span-3 text-right">Action</div>
              </div>
              {eligLoading && (
                <div className="p-6 text-center text-sm text-muted-foreground">Loading…</div>
              )}
              {eligError && (
                <div className="p-6 text-center text-sm text-destructive">
                  {eligErr instanceof Error ? eligErr.message : "Failed to load eligible members"}
                </div>
              )}
              {!eligLoading && !eligError && eligible.length === 0 && (
                <div className="p-6 text-center text-sm text-muted-foreground">
                  No eligible members yet
                </div>
              )}
              {eligible.map((m) => {
                const done = m.total > 0 && m.visited >= m.total;
                const issued = !!m.certificate_number;
                return (
                  <div
                    key={m.id}
                    className="grid grid-cols-12 items-center gap-2 border-b border-border px-4 py-3 text-sm last:border-0"
                  >
                    <div className="col-span-4 font-semibold">{m.full_name || "Unnamed"}</div>
                    <div className="col-span-3 text-muted-foreground">{m.phone || "—"}</div>
                    <div className="col-span-2">
                      {m.visited}/{m.total}
                    </div>
                    <div className="col-span-3 flex justify-end gap-2">
                      {issued ? (
                        <button
                          onClick={() => downloadPdf(m.certificate_url, m.certificate_number || "")}
                          className="btn-outline py-1.5 text-xs"
                        >
                          <Download className="h-3 w-3" />
                          PDF
                        </button>
                      ) : done ? (
                        <button onClick={() => issue(m)} className="btn-primary py-1.5 text-xs">
                          <FileCheck className="h-3 w-3" />
                          Issue
                        </button>
                      ) : (
                        <span className="text-xs text-muted-foreground">In progress</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {tab === "issued" && (
        <div className="-mx-4 overflow-hidden rounded-2xl border border-border bg-card sm:mx-0">
          <div className="overflow-x-auto">
            <div className="min-w-[520px]">
              <div className="grid grid-cols-12 gap-2 border-b border-border px-4 py-3 text-xs font-semibold text-muted-foreground">
                <div className="col-span-3">Cert No</div>
                <div className="col-span-3">Member</div>
                <div className="col-span-2">Visited</div>
                <div className="col-span-2">Date</div>
                <div className="col-span-2 text-right">Action</div>
              </div>
              {issuedLoading && (
                <div className="p-6 text-center text-sm text-muted-foreground">Loading…</div>
              )}
              {issuedError && (
                <div className="p-6 text-center text-sm text-destructive">
                  {issuedErr instanceof Error ? issuedErr.message : "Failed to load certificates"}
                </div>
              )}
              {!issuedLoading && !issuedError && completedCerts.length === 0 && (
                <div className="p-6 text-center text-sm text-muted-foreground">
                  No certificates issued yet
                </div>
              )}
              {completedCerts.map((c) => (
                <div
                  key={c.id}
                  className="grid grid-cols-12 items-center gap-2 border-b border-border px-4 py-3 text-sm last:border-0"
                >
                  <div className="col-span-3 font-mono text-xs">{c.certificate_number}</div>
                  <div className="col-span-3 font-semibold">{c.member_name}</div>
                  <div className="col-span-2">
                    {c.visited}/{c.total}
                  </div>
                  <div className="col-span-2 text-muted-foreground">{fmtDate(c.issued_at)}</div>
                  <div className="col-span-2 flex justify-end">
                    <button
                      onClick={() => downloadPdf(c.certificate_url, c.certificate_number)}
                      className="btn-outline py-1.5 text-xs"
                    >
                      <Download className="h-3 w-3" />
                      PDF
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {tab === "settings" && settings && (
        <div className="space-y-4 rounded-2xl border border-border bg-card p-5">
          <div className="flex items-center gap-2">
            <Award className="h-5 w-5 text-primary" />
            <h3 className="font-serif text-lg font-bold">Certificate Template</h3>
          </div>
          <p className="text-xs text-muted-foreground">
            These texts appear on the generated certificate. Set translations for each language.
          </p>

          {(["en", "hi", "gu"] as const).map((l) => (
            <div key={l} className="rounded-xl border border-border p-4">
              <div className="mb-2 text-xs font-bold uppercase text-primary">
                {l === "en" ? "English" : l === "hi" ? "हिन्दी" : "ગુજરાતી"}
              </div>
              <label className="mb-1 block text-xs font-semibold">Title</label>
              <input
                className="mb-2 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
                value={settings[`title_${l}` as keyof CertSettings] as string}
                onChange={(e) => setSettings({ ...settings, [`title_${l}`]: e.target.value })}
              />
              <label className="mb-1 block text-xs font-semibold">Message</label>
              <textarea
                rows={3}
                className="mb-2 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
                value={settings[`message_${l}` as keyof CertSettings] as string}
                onChange={(e) => setSettings({ ...settings, [`message_${l}`]: e.target.value })}
              />
              <label className="mb-1 block text-xs font-semibold">Signatory Title</label>
              <input
                className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
                value={settings[`signatory_title_${l}` as keyof CertSettings] as string}
                onChange={(e) =>
                  setSettings({ ...settings, [`signatory_title_${l}`]: e.target.value })
                }
              />
            </div>
          ))}

          <div>
            <label className="mb-1 block text-xs font-semibold">Signatory Name</label>
            <input
              className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
              value={settings.signatory_name}
              onChange={(e) => setSettings({ ...settings, signatory_name: e.target.value })}
            />
          </div>

          <button
            onClick={saveSettings}
            disabled={saveMut.isPending}
            className="btn-primary py-2 text-sm disabled:pointer-events-none disabled:opacity-60"
          >
            <Save className="h-4 w-4" />
            {saveMut.isPending ? "Saving..." : "Save Template"}
          </button>
        </div>
      )}
    </div>
  );
}
